const EARTZ_PRIVACY_SELECTOR = '[data-eartz-cookie-preferences]';
const EARTZ_PREFERENCE_STORAGE_KEYS = ['viewedProducts'];
const CONSENTMO_OPEN_RETRY_MS = 80;
const CONSENTMO_OPEN_TIMEOUT_MS = 0;

const CONSENTMO_ROMANIAN_TEXT = new Map([
  [
    'We use necessary cookies to operate the store. Optional analytics and marketing cookies remain off unless you choose to accept them.',
    'Folosim cookie-uri necesare pentru funcționarea magazinului. Cookie-urile opționale de analiză și marketing rămân dezactivate până când alegi să le accepți.',
  ],
  [
    'We use cookies to run this site, measure traffic, and personalize content. You can accept, reject, or manage your privacy choices anytime.',
    'Folosim cookie-uri necesare pentru funcționarea magazinului. Cookie-urile opționale de analiză și marketing rămân dezactivate până când alegi să le accepți.',
  ],
  ['Privacy Policy', 'Politica de confidențialitate'],
  ['Privacy Policy (opens in a new tab)', 'Politica de confidențialitate (se deschide într-o filă nouă)'],
  ['Close', 'Închide'],
  ['Preferences', 'Preferințe'],
  ['Reject', 'Respinge'],
  ['Accept', 'Acceptă'],
  ['Select the Types of Cookies You Accept', 'Alege tipurile de cookie-uri acceptate'],
  [
    'Choose which cookies we can use. Necessary cookies are always on. You can change your choices at any time in the cookie settings.',
    'Alege ce cookie-uri putem folosi. Cele necesare sunt mereu active, iar opțiunile pot fi schimbate oricând din setările cookie.',
  ],
  ['Necessary', 'Necesare'],
  [
    'Essential for the website to function properly, these cookies enable core features such as security, page navigation, and access to secure areas. They cannot be disabled.',
    'Aceste cookie-uri sunt necesare pentru securitate, navigare și funcțiile de bază ale magazinului. Nu pot fi dezactivate.',
  ],
  ['Statistics', 'Analiză'],
  [
    'By collecting anonymous data, these cookies help us understand how visitors interact with the website, allowing us to improve performance and user experience.',
    'Aceste cookie-uri ne ajută să înțelegem, prin date agregate, cum este folosit magazinul și unde poate fi îmbunătățit.',
  ],
  ['Marketing', 'Marketing'],
  [
    'These cookies track your activity across websites to deliver personalized ads and relevant content. They also help measure the effectiveness of our marketing campaigns.',
    'Aceste cookie-uri pot fi folosite pentru conținut publicitar relevant și pentru măsurarea campaniilor de marketing.',
  ],
  [
    'These cookies remember your choices - such as language preferences or login details - to provide a more personalized experience.',
    'Aceste cookie-uri rețin opțiuni precum limba selectată sau detalii de autentificare, pentru o experiență mai potrivită.',
  ],
  ['Save choices', 'Salvează opțiunile'],
  ['Reject All', 'Respinge tot'],
  ['Accept All', 'Acceptă tot'],
]);

const CONSENTMO_ENGLISH_TEXT = new Map([
  [
    'We use cookies to run this site, measure traffic, and personalize content. You can accept, reject, or manage your privacy choices anytime.',
    'We use necessary cookies to operate the store. Optional analytics and marketing cookies remain off unless you choose to accept them.',
  ],
]);

const translatedConsentmoRoots = new WeakMap();
const pendingConsentmoOpenRequests = new WeakSet();

function invokeConsentmoPreferences() {
  if (window.Consentmo && typeof window.Consentmo.openDialog === 'function') {
    window.Consentmo.openDialog('preferences');
    return true;
  }

  if (typeof window.showPreferences === 'function') {
    window.showPreferences();
    return true;
  }

  return false;
}

function requestConsentmoPreferences(trigger) {
  if (pendingConsentmoOpenRequests.has(trigger)) return;

  const startedAt = Date.now();
  pendingConsentmoOpenRequests.add(trigger);
  trigger.setAttribute('aria-busy', 'true');

  const finishRequest = () => {
    trigger.removeAttribute('aria-busy');
    window.setTimeout(() => pendingConsentmoOpenRequests.delete(trigger), 500);
  };

  const attemptOpen = () => {
    try {
      if (invokeConsentmoPreferences()) {
        finishRequest();
        return;
      }
    } catch (error) {
      console.warn('Cookie preferences could not be opened yet.', error);
    }

    if (Date.now() - startedAt < CONSENTMO_OPEN_TIMEOUT_MS) {
      window.setTimeout(attemptOpen, CONSENTMO_OPEN_RETRY_MS);
      return;
    }

    finishRequest();
    window.location.assign(trigger.href);
  };

  attemptOpen();
}

function openConsentmoPreferences(event) {
  const trigger = event.target instanceof Element ? event.target.closest(EARTZ_PRIVACY_SELECTOR) : null;
  if (!trigger) return;

  event.preventDefault();
  requestConsentmoPreferences(trigger);
}

document.addEventListener('click', openConsentmoPreferences);

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;

  const trigger = event.target instanceof Element ? event.target.closest(EARTZ_PRIVACY_SELECTOR) : null;
  if (!trigger) return;

  event.preventDefault();
  requestConsentmoPreferences(trigger);
});

function clearWithdrawnPreferenceData() {
  if (!window.Shopify?.customerPrivacy) return;
  if (window.Shopify.customerPrivacy.preferencesProcessingAllowed?.() === true) return;

  EARTZ_PREFERENCE_STORAGE_KEYS.forEach((key) => window.localStorage.removeItem(key));
}

if (window.Shopify?.customerPrivacy) {
  clearWithdrawnPreferenceData();
} else {
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', clearWithdrawnPreferenceData, { once: true });
}

document.addEventListener('visitorConsentCollected', clearWithdrawnPreferenceData);

function replaceConsentmoText(root, translations) {
  if (!root) return;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();

  while (node) {
    const original = node.nodeValue || '';
    const trimmed = original.trim();
    const translation = translations.get(trimmed);

    if (translation && translation !== trimmed) {
      node.nodeValue = original.replace(trimmed, translation);
    }

    node = walker.nextNode();
  }

  root.querySelectorAll('[aria-label], [title]').forEach((element) => {
    ['aria-label', 'title'].forEach((attribute) => {
      const value = element.getAttribute(attribute);
      const translation = value ? translations.get(value.trim()) : null;
      if (translation && translation !== value) element.setAttribute(attribute, translation);
    });
  });
}

function syncConsentmoPrivacyLink(root) {
  const localizedRoot = window.Shopify?.routes?.root || '/';
  const privacyPolicyUrl = `${localizedRoot}policies/privacy-policy`;

  root.querySelectorAll('a').forEach((link) => {
    const label = link.textContent.trim();
    if (label === 'Privacy Policy' || label === 'Politica de confidențialitate') {
      if (link.getAttribute('href') !== privacyPolicyUrl) {
        link.setAttribute('href', privacyPolicyUrl);
      }
    }
  });
}

function attachConsentmoTranslation(host) {
  if (
    !(host instanceof HTMLElement) ||
    !host.shadowRoot ||
    translatedConsentmoRoots.has(host.shadowRoot)
  ) {
    return;
  }

  const root = host.shadowRoot;
  const isRomanian = document.documentElement.lang.toLowerCase().startsWith('ro');
  const translations = isRomanian ? CONSENTMO_ROMANIAN_TEXT : CONSENTMO_ENGLISH_TEXT;
  let updateScheduled = false;
  let observer;

  const observe = () => {
    observer.observe(root, {
      childList: true,
      subtree: true,
    });
  };

  const updateConsentmoCopy = () => {
    updateScheduled = false;
    observer.disconnect();
    syncConsentmoPrivacyLink(root);
    replaceConsentmoText(root, translations);
    observe();
  };

  observer = new MutationObserver(() => {
    if (updateScheduled) return;
    updateScheduled = true;
    window.requestAnimationFrame(updateConsentmoCopy);
  });

  translatedConsentmoRoots.set(root, observer);
  updateConsentmoCopy();
}

function findConsentmoRoot() {
  const host = document.querySelector('csm-cookie-consent, .csm-cmp-root');
  if (host) attachConsentmoTranslation(host);
}

document.addEventListener('csm-cookie-consent-ready', (event) => {
  attachConsentmoTranslation(event.target);
});

const consentmoHostObserver = new MutationObserver(findConsentmoRoot);
consentmoHostObserver.observe(document.documentElement, { childList: true, subtree: true });
findConsentmoRoot();
